import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Outlet, useLocation, useNavigate } from "react-router";
import { postWebViewMessage } from "./utlis/webViewBridge";
import ProgressiveLoader from "./components/loader/ProgressiveLoader";
import SnackBar from "./components/snack-bar/SnackBar";
import useFontScale from "./hooks/useFontScale";
import { getParams } from "./utlis/helpers";
import { getCustomTheme } from "./services/websiteApi";
import EmailVerification from "./components/emailVerification/EmailVerification";
import { FORGOT_PASSWORD, LOGIN, SIGNUP, USER_LIST } from "./routes/routerPath";
import {
  buildSessionPath,
  getStoredSession,
  openNotificationDestination,
} from "./components/mmtUserList/mmtFlow";
import { isReportRoute } from "./utlis/lastVisitedUrl";

const AUTH_PATHS = new Set(["/", LOGIN, SIGNUP, FORGOT_PASSWORD]);
const PENDING_PUSHES_KEY = "mmtPendingNativePushes";

const App = () => {
  const { loading, error } = useSelector((state) => state.global);
  const [loadingTheme, setLoadingTheme] = useState(false);
  const { providerId, adminId, clientId, emailVerificationToken } = getParams();
  const { pathname, search, hash } = useLocation();
  const navigate = useNavigate();
  const isAuthPath = AUTH_PATHS.has(pathname);
  // Initialize font scaling listener
  useFontScale();

  // Remember the last navigable app URL so a fresh launch can restore the
  // user's place. Report previews are intentionally transient: public preview
  // tabs share this origin and must never replace the originating app page.
  useEffect(() => {
    if (!isAuthPath && !isReportRoute(pathname)) {
      localStorage.setItem("lastVisitedUrl", pathname + search + hash);
    }
  }, [pathname, search, hash, isAuthPath]);

  // Give the native WebView shell the authenticated session it needs to
  // register its FCM token with the notification API.
  useEffect(() => {
    const session = getStoredSession();
    if (session) {
      postWebViewMessage("SESSION_ESTABLISHED", {
        accessToken: session.accessToken,
        staffId: session.staffId,
        locale: session.locale || "en",
      });
    }
  }, [pathname, search]);

  // The native shell owns FCM. It forwards notification taps into the page so
  // web and native notifications share the exact same Quick Jump behavior.
  useEffect(() => {
    const handleNativeNotification = (event) => {
      let data = event.data;
      if (typeof data === "string") {
        try {
          data = JSON.parse(data);
        } catch {
          return;
        }
      }
      if (data?.type === "NATIVE_NOTIFICATION_RECEIVED" && data.payload) {
        let pending = [];
        try {
          pending = JSON.parse(
            sessionStorage.getItem(PENDING_PUSHES_KEY) || "[]",
          );
        } catch {
          pending = [];
        }
        const isNew = !pending.some((item) => item.id === data.payload.id);
        const next = isNew ? [...pending, data.payload] : pending;
        sessionStorage.setItem(PENDING_PUSHES_KEY, JSON.stringify(next));
        if (isNew) {
          window.dispatchEvent(
            new CustomEvent("native-notification-received", {
              detail: data.payload,
            }),
          );
        }
        return;
      }
      if (data?.type !== "NATIVE_NOTIFICATION_OPENED") return;
      openNotificationDestination(data.payload, getStoredSession(), navigate);
    };

    window.addEventListener("message", handleNativeNotification);
    return () =>
      window.removeEventListener("message", handleNativeNotification);
  }, [navigate]);

  // Hardware back button bridge: the native WebView shell calls this on
  // Android back press. Go back within the SPA history when possible,
  // then fall back to the web user list; BACK_TO_USER_LIST is only posted
  // when there is nowhere left to go (shells may use it to exit).
  useEffect(() => {
    window.__handleNativeBack = () => {
      const canGoBack = (window.history.state?.idx ?? 0) > 0;
      if (canGoBack) {
        navigate(-1);
        return;
      }
      const session = getStoredSession();
      if (session && pathname !== USER_LIST && !AUTH_PATHS.has(pathname)) {
        navigate(buildSessionPath(USER_LIST, session), { replace: true });
        return;
      }
      postWebViewMessage("BACK_TO_USER_LIST");
    };
    return () => {
      delete window.__handleNativeBack;
    };
  }, [navigate, pathname]);

  const callThemeAPI = async () => {
    await getCustomTheme()
      .then((res) => {
        sessionStorage.setItem("customtheme", JSON.stringify(res?.data?.data));
        document.documentElement.style.setProperty(
          "--primary-color",
          res?.data?.data?.primaryColorCode,
        );
        document.documentElement.style.setProperty(
          "--secondary-color",
          res?.data?.data?.secondaryColorCode,
        );
        setLoadingTheme(true);
      })
      .catch(() => {
        setLoadingTheme(true);
      });
  };
  useEffect(() => {
    if (emailVerificationToken) return;
    if (providerId && adminId && clientId) {
      callThemeAPI();
    } else {
      setLoadingTheme(true);
    }
  }, []);

  if (emailVerificationToken) {
    return <EmailVerification token={emailVerificationToken} />;
  }

  return (
    <div
      className={`mobile-app-shell ${isAuthPath ? "auth-shell" : ""} ${pathname === "/people-stats" ? "stats-shell" : ""}`}
    >
      {loadingTheme && <Outlet />}
      {loading && <ProgressiveLoader loader={loading} />}
      <SnackBar error={error} />
    </div>
  );
};

export default App;
